import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { differenceInDays, parseISO } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from "@/lib/toast";
import { ReservationActionPanel } from '@/components/reservations/drawer-sections/ReservationActionPanel';
import { ReservationGuestSection } from '@/components/reservations/drawer-sections/ReservationGuestSection';
import { ReservationHeader } from '@/components/reservations/drawer-sections/ReservationHeader';
import { ReservationSourceSection } from '@/components/reservations/drawer-sections/ReservationSourceSection';
import { ReservationStaySection } from '@/components/reservations/drawer-sections/ReservationStaySection';
import { RESERVATION_STATUS_MAP } from '@/components/reservations/drawer-sections/reservationDrawerStatus';
import {
  useBookingQuery,
  useCancelBookingMutation,
  useUpdateBookingMutation,
} from '@/hooks/api/use-bookings-query';
import { useRoomsQuery } from '@/hooks/api/use-rooms-query';
import { api } from '@/lib/axios';
import { cn } from '@/lib/utils';

interface ReservationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservationId: string | null;
}

export function ReservationDrawer({
  open,
  onOpenChange,
  reservationId,
}: ReservationDrawerProps) {
  const { data: booking, isLoading } = useBookingQuery(reservationId ?? '');
  const { data: rooms = [] } = useRoomsQuery();
  const roomsById = useMemo(() => new Map(rooms.map((room) => [room.id, room])), [rooms]);

  const [isEditing, setIsEditing] = useState(false);
  const [editArrival, setEditArrival] = useState('');
  const [editDeparture, setEditDeparture] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [noShowPending, setNoShowPending] = useState(false);

  const updateMutation = useUpdateBookingMutation();
  const cancelMutation = useCancelBookingMutation();
  const queryClient = useQueryClient();

  const handleEditSave = () => {
    if (!booking) return;

    updateMutation.mutate(
      {
        id: booking.id,
        ...(editArrival ? { arrival_date: editArrival } : {}),
        ...(editDeparture ? { departure_date: editDeparture } : {}),
        updated_at: booking.updated_at,
      },
      {
        onSuccess: () => {
          toast.success('Reserva atualizada!');
          setIsEditing(false);
        },
        onError: () => toast.error('Erro ao atualizar reserva.'),
      },
    );
  };

  const handleCancel = () => {
    if (!booking) return;

    cancelMutation.mutate(
      { id: booking.id, reason: cancelReason || undefined },
      {
        onSuccess: () => {
          toast.success('Reserva cancelada.');
          setShowCancelConfirm(false);
          onOpenChange(false);
        },
        onError: () => toast.error('Erro ao cancelar reserva.'),
      },
    );
  };

  const handleNoShow = async () => {
    if (!booking) return;
    setNoShowPending(true);

    try {
      await api.post(`/v1/reservas/${booking.id}/nao-comparecimento`);
      toast.success('Reserva marcada como No-Show.');
      queryClient.invalidateQueries({ queryKey: ['booking', String(booking.id)] });
      onOpenChange(false);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Erro ao marcar no-show.';
      toast.error(msg);
    } finally {
      setNoShowPending(false);
    }
  };

  if (!open) return null;

  const guest = booking?.main_guest ?? booking?.guest;
  const nights =
    booking && booking.departure_date && booking.arrival_date
      ? differenceInDays(parseISO(booking.departure_date), parseISO(booking.arrival_date))
      : 0;
  const statusKey = booking ? RESERVATION_STATUS_MAP[booking.status] : 'pending';
  const allocatedRoomId = booking?.rooms?.[0]?.allocated_room_id;
  const allocatedRoom = allocatedRoomId != null ? roomsById.get(allocatedRoomId) ?? null : null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={() => onOpenChange(false)}
      />

      <div
        className={cn(
          'fixed right-0 top-0 h-full w-full max-w-lg',
          'bg-card border-l border-border/50',
          'z-50 shadow-2xl',
          'transform transition-transform duration-300 ease-out',
          'overflow-y-auto',
        )}
      >
        <ReservationHeader
          booking={booking}
          reservationId={reservationId}
          statusKey={statusKey}
          onClose={() => onOpenChange(false)}
        />

        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="p-4 space-y-6">
            <ReservationGuestSection guest={guest} />
            <ReservationStaySection
              booking={booking}
              allocatedRoom={allocatedRoom}
              allocatedRoomId={allocatedRoomId}
              nights={nights}
            />
            <ReservationSourceSection source={booking?.source} />
          </div>
        )}

        <div className="sticky bottom-0 bg-card/95 backdrop-blur-sm border-t border-border/50 p-4 space-y-3">
          <ReservationActionPanel
            booking={booking}
            isEditing={isEditing}
            showCancelConfirm={showCancelConfirm}
            cancelReason={cancelReason}
            noShowPending={noShowPending}
            updatePending={updateMutation.isPending}
            cancelPending={cancelMutation.isPending}
            onCancelReasonChange={setCancelReason}
            onShowCancelConfirm={setShowCancelConfirm}
            onEditingChange={setIsEditing}
            onEditArrivalChange={setEditArrival}
            onEditDepartureChange={setEditDeparture}
            onEditSave={handleEditSave}
            onCancelBooking={handleCancel}
            onNoShow={() => void handleNoShow()}
          />
        </div>
      </div>
    </>
  );
}
